import { Grid, Card, CardHeader, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { AccountBalanceWallet, Payments, Warning, ReceiptLong } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { MiniBarChart } from '../../components/MiniBarChart';
import { EmptyState } from '../../components/EmptyState';
import { clientPortalApi } from '../../api/clientPortal.api';

const inr = (x = 0) => `₹${Number(x).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'success' | 'warning' | 'error' => (s === 'reconciled' ? 'success' : s === 'disputed' ? 'error' : 'warning');

export function ClientFinancePage() {
  const { data } = useQuery({ queryKey: ['client-portal-finance'], queryFn: () => clientPortalApi.finance() });

  return (
    <>
      <PageHeader title="Finance Reconciliation" subtitle="Invoices raised by MyevCRM, dealer-wise — outstanding & ageing." />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={6} sm={3}><StatCard gradient color="#1e40af" label="Total Invoiced" value={inr(data?.totalInvoiced)} icon={<ReceiptLong />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard gradient color="#16a34a" label="Paid" value={inr(data?.paid)} icon={<Payments />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard gradient color="#d97706" label="Outstanding" value={inr(data?.outstanding)} icon={<AccountBalanceWallet />} /></Grid>
        <Grid item xs={6} sm={3}><StatCard gradient color="#dc2626" label="Overdue" value={inr(data?.overdue)} icon={<Warning />} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}><CardHeader title="Invoice Ageing" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent>{(data?.ageing.some((a) => a.value > 0)) ? <MiniBarChart data={(data?.ageing ?? []).map((d) => ({ ...d, color: '#1e40af' }))} valueFormatter={(v) => inr(v)} /> : <EmptyState title="No data yet" dense />}</CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}><CardHeader title="Dealer-wise" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.dealerWise.length ?? 0) === 0 ? <EmptyState title="No dealers" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Dealer</TableCell><TableCell>Invoiced</TableCell><TableCell>Paid</TableCell><TableCell>Outstanding</TableCell></TableRow></TableHead>
                  <TableBody>{data!.dealerWise.map((d) => <TableRow key={d.dealerId}><TableCell>{d.dealerName}</TableCell><TableCell>{inr(d.invoiced)}</TableCell><TableCell>{inr(d.paid)}</TableCell><TableCell>{inr(d.outstanding)}</TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
        <Grid item xs={12}>
          <Card><CardHeader title="Invoices" titleTypographyProps={{ variant: 'subtitle1' }} />
            <CardContent sx={{ p: 0, overflowX: 'auto' }}>
              {(data?.invoices.length ?? 0) === 0 ? <EmptyState title="No invoices" dense /> : (
                <Table size="small"><TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>Dealer</TableCell><TableCell>Amount</TableCell><TableCell>Due</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>{data!.invoices.map((inv, idx) => <TableRow key={idx}><TableCell>{inv.paymentCode}</TableCell><TableCell>{inv.dealerName}</TableCell><TableCell>{inr(inv.amount)}</TableCell><TableCell>{inv.dueDate ?? '—'}</TableCell><TableCell><Chip size="small" color={statusColor(inv.status)} label={inv.status} /></TableCell></TableRow>)}</TableBody></Table>
              )}
            </CardContent></Card>
        </Grid>
      </Grid>
    </>
  );
}
